import React, { Suspense, useEffect } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Sidebar from './components/Sidebar/Sidebar';
import Spinner from './components/Spinner/Spinner';
import { AuthContext } from './components/Auth/AuthContext/AuthContext';
import './styles/tailwind.css';
import '@aws-amplify/ui-react/styles.css';
import './styles/authenticatorStyles.css';
import { initializeAmplitude } from 'shared/lib/eventTracking';
import AuthModal from './components/Auth/AuthModal/AuthModal';
import MetaHead from './components/MetaHead/MetaHead';
import PageLoader from './components/PageLoader/PageLoader';
import { ShareMusicProvider } from './contexts/ShareMusicContext';
import GlobalShareMusicPanel from './components/GlobalShareMusicPanel/GlobalShareMusicPanel';
import { isPublicRoute, getAndClearLoginRedirectUrl } from './utils/authUtils';

initializeAmplitude(process.env.REACT_APP_AMPLITUDE_API_KEY!);

// Preload critical route components after initial load
const preloadCriticalComponents = () => {
  // Only preload if user is authenticated to avoid unnecessary downloads
  setTimeout(() => {
    // Tier 1: Most frequently accessed routes (sidebar + common pages)
    import(/* webpackPreload: true */ './pages/platform/Dashboard/Dashboard');
    import(/* webpackPreload: true */ './pages/platform/OurImpact/OurImpact');
    import(/* webpackPreload: true */ './pages/platform/Notifications/Notifications');
    import(/* webpackPreload: true */ './pages/platform/Settings/Settings');
    import(/* webpackPreload: true */ './pages/platform/Coral/Coral');
    import(/* webpackPreload: true */ './pages/platform/ListeningRoom/ListeningRoomPage');
    import(/* webpackPreload: true */ './pages/platform/Artist/ArtistPage');
  }, 1000); // Wait 1 second after initial load
};

// Preload user journey components (conditionally based on access level)
const preloadUserJourneyComponents = (isContributor: boolean) => {
  setTimeout(() => {
    // Always preload profile
    import(/* webpackPrefetch: true */ './pages/platform/UserProfile/UserProfilePage');

    // Preload create-coral for NON-contributors (so they can quickly become contributors)
    if (!isContributor) {
      import(/* webpackPrefetch: true */ './pages/platform/CreateCoralFunnel/CreateCoralFunnel');
    }
  }, 3000); // Wait 3 seconds to not interfere with critical preload
};

const App: React.FC = () => {
  const { showAuth, setShowAuth, currentUser, loading, fetchUserData, isContributor } = React.useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const handleAuthSuccess = React.useCallback(async () => {
    await fetchUserData();
    setShowAuth({ show: false, signIn: false });
    // Get the stored redirect URL (if any) or default to dashboard
    const redirectUrl = getAndClearLoginRedirectUrl() || '/dashboard';
    navigate(redirectUrl, { replace: true });
  }, [fetchUserData, setShowAuth, navigate]);

  const isPublicPage = React.useMemo(() => isPublicRoute(location.pathname), [location.pathname]);
  const shouldRenderSidebar = React.useMemo(
    () => currentUser !== null && isSidebarPage(location.pathname),
    [currentUser, location.pathname]
  );

  // Preload critical components when user is authenticated and app is loaded
  useEffect(() => {
    if (currentUser && !loading) {
      preloadCriticalComponents();
      preloadUserJourneyComponents(isContributor);
    }
  }, [currentUser, loading, isContributor]);

  // Public marketing pages render their (auth-independent) content immediately
  // rather than waiting behind the auth spinner. This lets the build-time
  // prerendered HTML hydrate flash-free, and is a prerequisite for the page
  // content to be present on first paint (see the public-pages prerendering
  // plan). App/auth routes keep the original spinner gate unchanged.
  if (loading && !isPublicPage) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <div 
      className={`${isPublicPage ? 'min-h-screen' : 'min-h-screen'} flex flex-col ${handleColorScheme(location.pathname)}`}
    >
      <MetaHead />
      {showAuth.show && !currentUser && (
        <AuthModal 
          isOpen={showAuth.show}
          onClose={() => setShowAuth({ show: false, signIn: false })}
          onAuthSuccess={handleAuthSuccess}
          initialSignIn={showAuth.signIn}
        />
      )}
      {isPublicPage ? (
        // Public page layout - natural content height
        <div className="flex flex-col min-h-screen">
          {location.pathname !== '/welcome' && (
            <Header
              background={handleHeaderColor(location.pathname)}
              navigationTextHandler={handleNavigationTextColor(location.pathname)}
            />
          )}
          <main className="flex-grow">
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </main>
          {location.pathname !== '/welcome' && <Footer />}
        </div>
      ) : (
        // Logged-in page layout - fixed sidebar with natural scroll content
        <ShareMusicProvider>
          {shouldRenderSidebar && (
            <Sidebar className="fixed top-0 left-0 right-0 z-50 w-full h-16 p-4 sm:w-20 sm:h-screen sm:bottom-0 sm:right-auto sm:py-10 sm:px-4" />
          )}
          <div className={`min-h-screen ${shouldRenderSidebar ? 'pt-16 sm:pt-0 sm:pl-20' : ''}`}>
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </div>
          <GlobalShareMusicPanel />
        </ShareMusicProvider>
      )}
    </div>
  );
};

function isSidebarPage(page: string): boolean {
  const pagesWithSidebar = ['/dashboard', '/coral', '/settings', '/notifications', '/activity', '/impact', '/artist', '/u', '/listening-room', '/community'];
  return pagesWithSidebar.some(p => page.startsWith(p));
}


function handleColorScheme(pathname: string): string {
  // Handle public coral pages
  if (pathname.startsWith('/c/')) {
    return 'bg-gradient-to-b from-primary-400 to-primary-800 text-white';
  }
  
  return '';
}

function handleHeaderColor(pathname: string): string {
  if (pathname.startsWith('/c/')) return 'bg-transparent text-white';
  return 'bg-white';
}

function handleNavigationTextColor(pathname: string): string {
  if (pathname.startsWith('/c/')) return 'text-gray-300 hover:text-white transition duration-200 text-xs md:text-sm lg:text-base';
  return 'text-gray-600 hover:text-gray-900 transition duration-200 text-sm md:text-base lg:text-lg';
}

export default App;
